import React, { ReactNode } from 'react';
import { Liquid } from 'liquidjs';
import { Template } from '../../interfaces/backend/Template';
import { Channels } from '../common/channelsEnum';
import { Alert } from 'antd';

const templateFieldsContainingMergeTags: {
  [c in Channels]: string[];
} = {
  [Channels.EMAIL]: ['html', 'subject', 'previewText'],
  [Channels.INAPP_WEB]: [
    'instant.title',
    'instant.imageURL',
    'instant.redirectURL',
    'batch.title',
    'batch.imageURL',
    'batch.redirectURL'
  ],
  [Channels.SMS]: ['text'],
  [Channels.CALL]: ['text'],
  [Channels.PUSH]: ['title', 'message'], //ToDo: Check this based on the final version of push channel
  [Channels.WEB_PUSH]: ['title', 'message', 'icon'] //ToDo: Check this based on the final version of web push channel
};

function getTemplateFieldsContainingMergeTags<Type>(
  template: Type
): (keyof Type)[] {
  return templateFieldsContainingMergeTags[
    (template as unknown as Template).channel
  ] as (keyof Type)[];
}

const unsubscribeURLRegex = /\{\{\s*notificationapi:unsubscribe_url\s*\}\}/g;

export const liquidizeWithoutMergeTags = (
  input: Record<string, string>
): {
  output?: Record<string, string>;
  error?: ReactNode;
  warning?: ReactNode;
} => {
  const liquid = new Liquid();
  const output: Record<string, string> = {};
  let warning;
  let error;
  try {
    let hasMissingParam = true;
    const fixedMockedMergeTags: Record<string, unknown> = {};
    const missingParams: string[] = [];
    let retries = 1000;
    while (hasMissingParam && retries > 0) {
      retries--;
      try {
        liquid.options.strictFilters = false;
        liquid.options.strictVariables = true;
        Object.keys(input).forEach((field) => {
          output[field] = liquid.parseAndRenderSync(
            input[field].replace(unsubscribeURLRegex, ''),
            fixedMockedMergeTags
          );
        });
        hasMissingParam = false;
      } catch (e) {
        const err = e as Error;

        if (err.message.startsWith('undefined variable')) {
          const variable = err.message.split(' ')[2].slice(0, -1);
          missingParams.push(variable);

          // given a string like alerts.0.title or alerts[0].title, we want to set fixedMockedMergeTags['alerts'][0]['title'] = 'mocked value'
          const variablePath = variable.split('.');
          let current = fixedMockedMergeTags;
          for (let i = 0; i < variablePath.length; i++) {
            if (i === variablePath.length - 1) {
              current[variablePath[i]] = `test${
                variablePath[i][0].toUpperCase() + variablePath[i].slice(1)
              }`;
            } else {
              if (Number.parseInt(variablePath[i + 1]) >= 0) {
                current[variablePath[i]] = [];
                // eslint-disable-next-line
                // @ts-ignore
                current = current[variablePath[i]];
              } else {
                current[variablePath[i]] = {};
                // eslint-disable-next-line
                // @ts-ignore
                current = current[variablePath[i]];
              }
            }
          }
        } else {
          hasMissingParam = false;
        }
      }
    }
    if (missingParams.length > 0) {
      // search for possible errors
      try {
        liquid.options.strictFilters = true;
        liquid.options.strictVariables = false;
        Object.keys(input).forEach((field) => {
          output[field] = liquid.parseAndRenderSync(
            (input[field] as string).replace(unsubscribeURLRegex, ''),
            fixedMockedMergeTags
          );
        });
      } catch (e) {
        const err = e as Error;
        warning = <Alert type="warning" description={err.message} />;
      }
    }
  } catch (e) {
    console.log(e);
    // hard errors, generally "Non-existent Tags": https://liquidjs.com/tutorials/options.html#Strict
    const err = e as Error;
    if (err.message.includes('not found')) {
      // sample error message: tag "adsasd" not found, line:1, col:58
      const tag = err.message.split('"')[1];
      error = (
        <Alert
          type="error"
          message="Invalid Tag"
          description={`{% ${tag} %} is not a valid tag. Perhaps you meant to use ${tag} in between {{ }}?`}
        />
      );
    } else {
      error = <Alert type="error" description={err.message} />;
      window.Rollbar?.error('Unknown liquidjs error', error);
    }
  }

  return { output, error, warning };
};

export const liquidizeTemplate = (
  template: Template
): { result?: Template; error?: ReactNode; warning?: ReactNode } => {
  const liquid = new Liquid();
  let result;
  let warning;
  let error;
  try {
    const updatedFields: Record<string, string> = {};
    let hasMissingParam = true;
    const fixedMockedMergeTags: Record<string, unknown> = {};
    const missingParams: string[] = [];
    let retries = 1000;
    while (hasMissingParam && retries > 0) {
      retries--;
      try {
        liquid.options.strictFilters = false;
        liquid.options.strictVariables = true;
        getTemplateFieldsContainingMergeTags<typeof template>(template)
          .filter((f) => template[f])
          .forEach((f) => {
            updatedFields[f] = liquid.parseAndRenderSync(
              (template[f] as string).replace(unsubscribeURLRegex, ''),
              fixedMockedMergeTags
            );
          });
        hasMissingParam = false;
      } catch (e) {
        const err = e as Error;

        if (err.message.startsWith('undefined variable')) {
          const variable = err.message.split(' ')[2].slice(0, -1);
          missingParams.push(variable);

          // given a string like alerts.0.title or alerts[0].title, we want to set fixedMockedMergeTags['alerts'][0]['title'] = 'mocked value'
          const variablePath = variable.split('.');
          let current = fixedMockedMergeTags;
          for (let i = 0; i < variablePath.length; i++) {
            if (i === variablePath.length - 1) {
              current[variablePath[i]] = `test${
                variablePath[i][0].toUpperCase() + variablePath[i].slice(1)
              }`;
            } else {
              if (Number.parseInt(variablePath[i + 1]) >= 0) {
                current[variablePath[i]] = [];
                // eslint-disable-next-line
                // @ts-ignore
                current = current[variablePath[i]];
              } else {
                current[variablePath[i]] = {};
                // eslint-disable-next-line
                // @ts-ignore
                current = current[variablePath[i]];
              }
            }
          }
        } else {
          hasMissingParam = false;
        }
      }
    }
    if (missingParams.length > 0) {
      // search for possible errors
      try {
        liquid.options.strictFilters = true;
        liquid.options.strictVariables = false;
        getTemplateFieldsContainingMergeTags<typeof template>(template)
          .filter((f) => template[f])
          .forEach((f) => {
            updatedFields[f] = liquid.parseAndRenderSync(
              (template[f] as string).replace(unsubscribeURLRegex, ''),
              fixedMockedMergeTags
            );
          });
      } catch (e) {
        const err = e as Error;
        warning = <Alert type="warning" description={err.message} />;
      }
    }

    result = {
      ...template,
      ...updatedFields
    };
  } catch (e) {
    console.log(e);
    // hard errors, generally "Non-existent Tags": https://liquidjs.com/tutorials/options.html#Strict
    const err = e as Error;
    if (err.message.includes('not found')) {
      // sample error message: tag "adsasd" not found, line:1, col:58
      const tag = err.message.split('"')[1];
      error = (
        <Alert
          type="error"
          message="Invalid Tag"
          description={`{% ${tag} %} is not a valid tag. Perhaps you meant to use ${tag} in between {{ }}?`}
        />
      );
    } else {
      error = <Alert type="error" description={err.message} />;
      window.Rollbar?.error('Unknown liquidjs error', error);
    }
  }

  return { result, error, warning };
};

export const liquidizeTemplateWithMergeTags = (
  template: Template,
  mergeTags: Record<string, unknown>
): { result?: Template; errors: string[] } => {
  const liquid = new Liquid();
  let result;
  const errors = [];
  try {
    const updatedFields: Record<string, string> = {};
    getTemplateFieldsContainingMergeTags<typeof template>(template)
      .filter((f) => template[f])
      .forEach((f) => {
        updatedFields[f] = liquid.parseAndRenderSync(
          decodeMailto(
            (template[f] as string).replace(unsubscribeURLRegex, '')
          ),
          mergeTags
        );
      });

    result = {
      ...template,
      ...updatedFields
    };
  } catch (e) {
    console.log(e);
    // hard errors, generally "Non-existent Tags": https://liquidjs.com/tutorials/options.html#Strict
    const err = e as Error;
    if (err.message.includes('not found')) {
      // sample error message: tag "adsasd" not found, line:1, col:58
      const tag = err.message.split('"')[1];
      errors.push(
        `{% ${tag} %} is not a valid tag. Perhaps you meant to use ${tag} in between {{ }}?`
      );
    } else {
      errors.push(err.message);
      window.Rollbar?.error('Unknown liquidjs error', err);
    }
  }

  return { result, errors };
};

export const getAllTags: (templates: Template[]) => {
  mergeTagsList: Record<string, 'STRING' | 'OBJECT' | 'ARRAY'>;
  mergeTagsObject: Record<string, unknown>;
} = (templates: Template[]) => {
  const liquid = new Liquid();
  const mergeTagsList: Record<string, 'STRING' | 'OBJECT' | 'ARRAY'> = {}; // flattened
  const mergeTagsObject: Record<string, unknown> = {}; // the object

  templates.forEach((template) => {
    let hasMissingParam = true;
    let retries = 10;
    while (hasMissingParam && retries > 0) {
      retries--;
      try {
        liquid.options.strictFilters = false;
        liquid.options.strictVariables = true;
        const passedInMergeTags = {
          ...mergeTagsObject,
          _items: [mergeTagsObject],
          _item: {
            ...mergeTagsObject
          }
        };
        for (let i = 0; i < 99; i++) {
          passedInMergeTags._items.push(mergeTagsObject);
        }
        getTemplateFieldsContainingMergeTags<typeof template>(template).forEach(
          (f) => {
            let input = template[f];
            if (f.includes('.')) {
              const [parent, child] = f.split('.');
              // eslint-disable-next-line
              input = (template as any)[parent][child];
            }
            liquid.parseAndRenderSync(
              (input as string).replace(unsubscribeURLRegex, ''),
              passedInMergeTags
            );
          }
        );
        hasMissingParam = false;
      } catch (e) {
        const err = e as Error;

        if (err.message.startsWith('undefined variable')) {
          let variable = err.message.split(' ')[2].slice(0, -1);
          let variablePath = variable.split('.');

          if (variablePath[0] === '_items' && variablePath.length === 3) {
            variable = variablePath.slice(2).join('.');
            variablePath = variable.split('.');
          }

          if (variablePath[0] === '_item' && variablePath.length === 2) {
            variable = variablePath.slice(1).join('.');
            variablePath = variable.split('.');
          }

          // add to a flat list of mergetags (no children), e.g. firstname, alerts, etc.
          if (variablePath.length === 1) {
            mergeTagsList[variable] = 'STRING';
          } else {
            if (Number.parseInt(variablePath[1]) >= 0) {
              mergeTagsList[variablePath[0]] = 'ARRAY';
            } else {
              mergeTagsList[variablePath[0]] = 'OBJECT';
            }
          }

          // given a string like alerts.0.title or alerts[0].title, we want to set fixedMockedMergeTags['alerts'][0]['title'] = 'mocked value'
          let current = mergeTagsObject;
          for (let i = 0; i < variablePath.length; i++) {
            if (i === variablePath.length - 1) {
              current[variablePath[i]] = `test${
                variablePath[i][0].toUpperCase() + variablePath[i].slice(1)
              }`;
            } else {
              if (Number.parseInt(variablePath[i + 1]) >= 0) {
                if (!Array.isArray(current[variablePath[i]])) {
                  current[variablePath[i]] = [];
                  // eslint-disable-next-line
                  // @ts-ignore
                  current = current[variablePath[i]];
                } else {
                  // eslint-disable-next-line
                  // @ts-ignore
                  current = current[variablePath[i]];
                }
              } else {
                if (typeof current[variablePath[i]] !== 'object') {
                  current[variablePath[i]] = {};
                  // eslint-disable-next-line
                  // @ts-ignore
                  current = current[variablePath[i]];
                } else {
                  // eslint-disable-next-line
                  // @ts-ignore
                  current = current[variablePath[i]];
                }
              }
            }
          }
        } else {
          hasMissingParam = false;
        }
      }
    }
  });

  return {
    mergeTagsList,
    mergeTagsObject
  };
};
export function decodeMailto(htmlString: string): string {
  // Regular expression to find the mailto link with optional recipient, subject, and body
  const mailtoRegex =
    /mailto:([^"?\s]*)(\?subject=([^&"\s]*))?(&body=([^&"\s]*))?"/g;

  // Replace function to decode URI components and format the string
  const decodedHtmlString = htmlString.replace(
    mailtoRegex,
    (match, p1, _p2, p3, _p4, p5) => {
      const decodedRecipient = p1 ? decodeURIComponent(p1) : '';
      const decodedSubject = p3 ? decodeURIComponent(p3) : '';
      const decodedBody = p5 ? decodeURIComponent(p5) : '';

      let result = `mailto:${decodedRecipient}`;
      if (match.includes('?subject=')) {
        result += `?subject=${decodedSubject}`;
      }
      if (match.includes('&body=')) {
        result += `&body=${decodedBody}`;
      }
      return result + '"';
    }
  );

  return decodedHtmlString;
}
